import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  getUserDetails,
  canEditSamplingFishData,
  handleLegacySessionExpired,
} from "../../components/content/axiosConfig";
import Swal from "sweetalert2";
import CustomModal from "../../components/custom/customModal";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { imageFieldToDataUrl } from "../../utils/imageFieldToDataUrl";
import PondDetailWorkflowPageHeader from "../../components/custom/PondDetailWorkflowPageHeader";
import PondDetailWorkflowTabs from "../../components/custom/PondDetailWorkflowTabs";

function speciesInitial(name) {
  const s = String(name || "").trim();
  return s ? s.charAt(0).toUpperCase() : "F";
}

const PondFishesViaPondDetail = () => {
    const [samples, setSamples] = useState([]);
    const [fishTypes, setFishTypes] = useState([]);
    const userDetails = getUserDetails();
    const fisherID = userDetails.FisherID;
    const canEditSamplingFish = canEditSamplingFishData(userDetails);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [entriesPerPage] = useState(10);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const { id } = useParams();
    const [pondFishType, setPondFishType] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [preview, setPreview] = useState(null);
    const [imageToShow, setImageToShow] = useState("");
    const [modalVisible, setModalVisible] = useState(false);
    const [pondName, setPondName] = useState('');
    const [pondDetailDisplayName, setPondDetailDisplayName] = useState('');
    const [fishType, setFishType] = useState(0);
    const [numberOfFishes, setNumberOfFishes] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);

    // Handle window resize for responsive design
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const openModal2 = (PondDFishID) => {
        setPondFishType(PondDFishID);
        setSelectedImage(null);
        setPreview(null);
        setIsModalOpen(true);
    };

    const closeModal2 = () => setIsModalOpen(false);

    const handleFileChange2 = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onload = () => setPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleUpload = async () => {
        setIsLoading(true);
        const formData = new FormData();
        if (selectedImage) {
            formData.append('image', selectedImage);
        }
        try {
            await axiosInstance.post(`/api/SamplingFishes/AddFishWithImage/${pondFishType}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            showSuccessToast('Image uploaded successfully');
            setIsModalOpen(false);
            FetchSamples();
        } catch (error) {
            if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
                const errorMessage = error.response?.data?.message || "Failed to upload image";
                showErrorToast(errorMessage);
            }
            console.error("Error uploading image:", error);
        } finally {
            setIsLoading(false);
            setIsModalOpen(false);
        }
    };

    const FetchSamples = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/api/SamplingFishes/GetAllFishesBYPondDID/${id}`, {
                params: { search: searchTerm }
            });
            const pondNotes = response.data;
            setSamples(pondNotes);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
                const errorMessage = error.response?.data?.message || "Failed to fetch Fish Species in Pond";
                showErrorToast(errorMessage);
            }
            console.error("Error fetching Fish Species in Pond:", error);
        } finally {
            setIsLoading(false);
        }
    }, [id, searchTerm]);

    const FetchFishTypes = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await axiosInstance.get(`/api/PondFishes/GetAllPondFishesViaPondDID/${id}`, {
                params: { search: '' }
            });
            const users = response.data;

            setFishTypes(users);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
                const errorMessage = error.response?.data?.message || "Failed to fetch fish types";
                showErrorToast(errorMessage);
            }
            console.error("Error fetching fish types:", error);
        }
    }, [id]);

    const CheckExpiry = useCallback(async () => {
        await CheckUserExpiry();
    }, []);

    // Debounce search implementation
    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            if (searchTerm !== undefined) {
                FetchSamples();
            }
        }, 800); // 800ms debounce delay

        return () => clearTimeout(debounceTimer);
    }, [searchTerm, FetchSamples]);

    useEffect(() => {
        checkTokenValidity();
        CheckExpiry();
        FetchFishTypes();
        FetchSamples();
    }, [FetchSamples, FetchFishTypes, CheckExpiry]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axiosInstance.get(`/api/PondDetail/GetPondDetail`, {
                    params: { id, FisherID: fisherID },
                });
                const name = res.data?.Name;
                if (!cancelled && name != null && String(name).trim()) {
                    setPondDetailDisplayName(String(name).trim());
                }
            } catch {
                /* optional label */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [id, fisherID]);

    const indexOfLastEntry = currentPage * entriesPerPage;
    const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
    const currentEntries = samples.slice(indexOfFirstEntry, indexOfLastEntry);
    const totalPages = Math.ceil(samples.length / entriesPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const DeleteUser = async (fishId) => {
        const result = await Swal.fire({
            title: "Delete this species?",
            text: "Associated sampling data for this species may be affected.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#14532d",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            setIsLoading(true);
            try {
                await axiosInstance.delete(`/api/SamplingFishes/DeleteFish/${fishId}`);
                const updatedStaffs = samples.filter((staff) => staff.PondFishesID !== fishId);
                setSamples(updatedStaffs);
                setIsLoading(false);
                showSuccessToast("Pond fish deleted successfully");
            } catch (error) {
                setIsLoading(false);
                if (error.response && error.response.status === 401) {
                    const auth = handleLegacySessionExpired(error);
                    showErrorToast(auth.message);
                } else {
                    const errorMessage =
                        error.response?.data?.message || "Failed to delete pond fish";
                    showErrorToast(errorMessage);
                }
                console.error("Error deleting pond fish:", error);
            }
        }
    };

    const pageNumbers = useMemo(() => {
        if (totalPages <= 5) return [...Array(totalPages)].map((_, i) => i + 1);
        const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
        return [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    }, [totalPages, currentPage]);

    const showFrom = samples.length === 0 ? 0 : indexOfFirstEntry + 1;
    const showTo = Math.min(indexOfLastEntry, samples.length);

    const renderRowActions = (web, alwaysVisible = false) => (
        <div className={`donezo-orders-actions${alwaysVisible ? " is-visible" : ""}`}>
            {web.imagePath ? (
                <button
                    type="button"
                    className="donezo-orders-actions__btn"
                    onClick={() => showImage(web.imagePath)}
                    title="View image"
                    aria-label="View fish image"
                >
                    <i className="fa fa-eye" aria-hidden />
                </button>
            ) : null}
            {canEditSamplingFish && (
                <>
                    <button
                        type="button"
                        className="donezo-orders-actions__btn"
                        onClick={() => openModal2(web.PondFishesID)}
                        title="Upload image"
                        aria-label="Upload fish image"
                    >
                        <i className="fa fa-upload" aria-hidden />
                    </button>
                    <button
                        type="button"
                        className="donezo-orders-actions__btn donezo-orders-actions__btn--danger"
                        onClick={() => DeleteUser(web.PondFishesID)}
                        title="Delete"
                        aria-label="Delete fish species"
                    >
                        <i className="fa fa-trash" aria-hidden />
                    </button>
                </>
            )}
        </div>
    );

    const openModal = (data = null) => {
        if (data) {
            setIsEditing(true);
            setEditId(data.PondFishesID);
            setPondName(data.PondID);
            setFishType(data.FishTypeID);
            setNumberOfFishes(data.NumberOfFishes);
        } else {
            setIsEditing(false);
            setEditId(null);
            setPondName("0");
            setFishType("0");
            setNumberOfFishes(0);
        }
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
    };

    const handleSubmit = async (e) => {
        if (canEditSamplingFish) {
            e.preventDefault();
            setIsLoading(true);
            try {
                if (!fishType || fishType === "0") {
                    showErrorToast("Please select a fish type");
                    setIsLoading(false);
                    return;
                }
                if (numberOfFishes < 0) {
                    showErrorToast("Number of fishes cannot be negative");
                    setIsLoading(false);
                    return;
                }

                if (isEditing) {
                    await axiosInstance.put(`/api/SamplingFishes/UpdateFish/${editId}`, {
                        PondDID: id,
                        FishTypeID: fishType,
                        FisherID: fisherID,
                        NumberOfFishes: numberOfFishes,
                        AverageLength: 0,
                        StandardDeviationLength: 0,
                        AverageWeight: 0,
                        StandardDeviationWeight: 0,
                        AverageWr: 0,
                        StandardDeviationWr: 0,
                        NumberOfFishesReleased: 0,
                        NumberOfFishesCulled: 0
                    });
                    showSuccessToast("Pond fish updated successfully");
                } else {
                    await axiosInstance.post(`/api/SamplingFishes/AddFish`, {
                        PondDID: id,
                        FishTypeID: fishType,
                        FisherID: fisherID,
                        NumberOfFishes: 0,
                        AverageLength: 0,
                        StandardDeviationLength: 0,
                        AverageWeight: 0,
                        StandardDeviationWeight: 0,
                        AverageWr: 0,
                        StandardDeviationWr: 0,
                        NumberOfFishesReleased: 0,
                        NumberOfFishesCulled: 0
                    });
                    showSuccessToast("Pond fish added successfully");
                }
                FetchSamples();
                setIsLoading(false);
                closeModal();
            } catch (error) {
                setIsLoading(false);
                if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
                    const errorMessage = error.response?.data?.message || "Failed to save pond fish";
                    showErrorToast(errorMessage);
                }
                console.error("Error submitting form:", error);
            }
        }
    };

    const showImage = (blobData) => {
        if (blobData == null) {
            showErrorToast("No image to display");
            return;
        }
        try {
            const src = imageFieldToDataUrl(blobData);
            if (!src) {
                showErrorToast("Could not display image");
                return;
            }
            setImageToShow(src);
            setModalVisible(true);
        } catch (err) {
            console.error(err);
            showErrorToast("Could not display image");
        }
    };
 

    if (isLoading) return <Preloader />;

    return (
        <div className="donezo-page donezo-list-page donezo-orders-page donezo-workflow-page donezo-upload-page">
            <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
                <PondDetailWorkflowPageHeader
                    pondDetailId={id}
                    section="pondFish"
                    detailName={pondDetailDisplayName}
                    noBottomMargin
                    actions={
                        canEditSamplingFish ? (
                            <button
                                type="button"
                                className="donezo-orders-toolbar__primary"
                                onClick={() => openModal()}
                            >
                                {windowWidth > 576 ? "Add species" : "Add"}
                            </button>
                        ) : null
                    }
                />

                <div className="mt-3">
                    <PondDetailWorkflowTabs id={id} active="pondFish" />
                </div>

                <div className="donezo-orders-toolbar mb-3">
                    <label htmlFor="pond-fish-search" className="visually-hidden">
                        Search species
                    </label>
                    <div className="donezo-orders-toolbar__search" style={{ maxWidth: "100%", flex: 1 }}>
                        <i className="fa fa-search" aria-hidden />
                        <input
                            id="pond-fish-search"
                            type="search"
                            className="form-control"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            autoComplete="off"
                        />
                    </div>
                </div>

                <div className="donezo-orders-panel">
                    <div className="d-none d-md-block table-responsive">
                        <table className="table align-middle mb-0 donezo-orders-table">
                            <thead>
                                <tr>
                                    <th>Species</th>
                                    <th className="text-center">Caught</th>
                                    <th className="text-center">Culled</th>
                                    <th className="text-center">Released</th>
                                    <th className="donezo-orders-table__actions-col" aria-label="Actions" />
                                </tr>
                            </thead>
                            <tbody>
                                {samples.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="p-0 border-0">
                                            <div className="donezo-empty-state">
                                                <div className="donezo-empty-state__icon" aria-hidden>
                                                    <i className="fa fa-fish" />
                                                </div>
                                                <p className="donezo-empty-state__title">No species found</p>
                                                <p className="donezo-empty-state__text">
                                                    {searchTerm
                                                        ? "Try a different search."
                                                        : "Add a species to start recording catches for this session."}
                                                </p>
                                                {canEditSamplingFish && !searchTerm && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary rounded-pill px-4 mt-3"
                                                        onClick={() => openModal()}
                                                    >
                                                        <i className="fa fa-plus me-2" aria-hidden />
                                                        Add species
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    currentEntries.map((web) => (
                                        <tr key={web.PondFishesID}>
                                            <td>
                                                <div className="donezo-orders-pond">
                                                    <span
                                                        className="donezo-orders-pond__avatar"
                                                        aria-hidden
                                                    >
                                                        {speciesInitial(web.FishTypeName)}
                                                    </span>
                                                    <div className="donezo-orders-pond__meta">
                                                        <span className="donezo-orders-pond__name">
                                                            {web.FishTypeName || "—"}
                                                        </span>
                                                        {web.PondName ? (
                                                            <span className="donezo-orders-pond__id">
                                                                {web.PondName}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-center">{web.NumberOfFishes ?? 0}</td>
                                            <td className="text-center">
                                                {web.NumberOfFishesCulled ?? 0}
                                            </td>
                                            <td className="text-center">
                                                {web.NumberOfFishesReleased ?? 0}
                                            </td>
                                            <td className="donezo-orders-table__actions-col">
                                                {renderRowActions(web)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-md-none donezo-orders-panel__mobile">
                        {samples.length === 0 ? (
                            <div className="donezo-empty-state donezo-empty-state--compact">
                                <p className="donezo-empty-state__title">No species found</p>
                                <p className="donezo-empty-state__text">
                                    {searchTerm ? "Try another search." : "No species to show."}
                                </p>
                            </div>
                        ) : (
                            currentEntries.map((web) => (
                                <div key={web.PondFishesID} className="donezo-mobile-card">
                                    <div className="donezo-mobile-card__head">
                                        <div className="donezo-orders-pond">
                                            <span className="donezo-orders-pond__avatar" aria-hidden>
                                                {speciesInitial(web.FishTypeName)}
                                            </span>
                                            <div className="donezo-orders-pond__meta">
                                                <h2 className="donezo-mobile-card__title mb-0">
                                                    {web.FishTypeName || "—"}
                                                </h2>
                                                {web.PondName ? (
                                                    <span className="donezo-orders-pond__id">
                                                        {web.PondName}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                        {renderRowActions(web, true)}
                                    </div>
                                    <div className="donezo-mobile-card__grid">
                                        <div>
                                            <span className="donezo-mobile-card__label">Caught</span>
                                            <div className="donezo-mobile-card__value">
                                                {web.NumberOfFishes ?? 0}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="donezo-mobile-card__label">Culled</span>
                                            <div className="donezo-mobile-card__value">
                                                {web.NumberOfFishesCulled ?? 0}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="donezo-mobile-card__label">Released</span>
                                            <div className="donezo-mobile-card__value">
                                                {web.NumberOfFishesReleased ?? 0}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {samples.length > 0 && (
                        <div className="donezo-orders-footer">
                            <div className="donezo-orders-footer__meta">
                                Showing {showFrom}-{showTo} from {samples.length}
                            </div>
                            <ul className="donezo-orders-pagination">
                                <li>
                                    <button
                                        type="button"
                                        className="donezo-orders-pagination__btn"
                                        disabled={currentPage === 1}
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        aria-label="Previous page"
                                    >
                                        <i className="fa fa-chevron-left" aria-hidden />
                                    </button>
                                </li>
                                {windowWidth < 768 ? (
                                    <li>
                                        <span className="donezo-orders-pagination__btn is-active">
                                            {currentPage}
                                        </span>
                                    </li>
                                ) : (
                                    pageNumbers.map((page, idx) => {
                                        const prev = pageNumbers[idx - 1];
                                        const showEllipsis = prev != null && page - prev > 1;
                                        return (
                                            <li
                                                key={page}
                                                className="d-inline-flex align-items-center gap-1"
                                            >
                                                {showEllipsis && (
                                                    <span className="donezo-orders-pagination__ellipsis">
                                                        …
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    className={`donezo-orders-pagination__btn${
                                                        page === currentPage ? " is-active" : ""
                                                    }`}
                                                    onClick={() => handlePageChange(page)}
                                                >
                                                    {page}
                                                </button>
                                            </li>
                                        );
                                    })
                                )}
                                <li>
                                    <button
                                        type="button"
                                        className="donezo-orders-pagination__btn"
                                        disabled={currentPage === totalPages || totalPages === 0}
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        aria-label="Next page"
                                    >
                                        <i className="fa fa-chevron-right" aria-hidden />
                                    </button>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>

                <CustomModal
                    isOpen={modalVisible}
                    onClose={() => setModalVisible(false)}
                    title="Fish Image"
                    showButton
                    showCancelButton
                    showSubmitButton={false}
                    modalSize="modal-lg"
                    minHeight="600px"
                >
                    <div className="d-flex align-items-center justify-content-center bg-white w-100 h-100">
                        <img
                            src={imageToShow}
                            alt="Fish"
                            style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                        />
                    </div>
                </CustomModal>

                <CustomModal
                    isOpen={isModalOpen}
                    onClose={closeModal2}
                    onSubmit={handleUpload}
                    title="Select Image"
                    showButton
                    showSubmitButton
                    showCancelButton
                    modalSize="modal-lg"
                    minHeight="auto"
                >
                    <p className="donezo-page-fine-print mb-2">Size should be lower than 4MB.</p>
                    <input type="file" accept="image/*" onChange={handleFileChange2} />
                    {preview && (
                        <div className="pt-3">
                            <img
                                src={preview}
                                alt="Selected Preview"
                                style={{
                                    width: "100%",
                                    maxHeight: "400px",
                                    objectFit: "contain",
                                }}
                            />
                        </div>
                    )}
                </CustomModal>

                <CustomModal
                    isOpen={modalIsOpen}
                    onClose={closeModal}
                    title={isEditing ? "Edit species" : "Add species"}
                    showButton
                    showCancelButton
                    showSubmitButton
                    modalSize="modal-md"
                    minHeight="auto"
                    onSubmit={handleSubmit}
                >
                    <div className="mt-2">
                        <label className="form-label small fw-medium" htmlFor="FishType">
                            Fish type *
                        </label>
                        <select
                            className="form-control"
                            id="FishType"
                            name="FishType"
                            disabled={isEditing}
                            value={fishType}
                            onChange={(e) => setFishType(e.target.value)}
                        >
                            <option value="0">Select fish type</option>
                            {fishTypes.map((web) => (
                                <option key={web.FishTypeID} value={web.FishTypeID}>
                                    {web.FishTypeName}
                                </option>
                            ))}
                        </select>
                    </div>
                </CustomModal>
            </div>
        </div>
    );
};

export default PondFishesViaPondDetail;